import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { WalletDto } from '@model/WalletDto'

import { WithdrawalStartLocationState } from './types'
import { getWithdrawalWalletDisabledReason } from './useWithdrawalWallets'

export const useWithdrawalSelections = (params: {
  wallets: WalletDto[]
  hasNegativeEquity: boolean
  isWalletsLoading: boolean
  isEquityLoading: boolean
  isCFD: boolean
}) => {
  const { wallets, hasNegativeEquity, isWalletsLoading, isEquityLoading, isCFD } = params
  const navigate = useNavigate()
  const location = useLocation()
  const { id: idParam } = useParams<{ id?: string }>()

  const locationState = useMemo(() => {
    const state = (location.state || {}) as WithdrawalStartLocationState
    return {
      ...state,
      isDestinationPreselected:
        state.isDestinationPreselected ?? Boolean(idParam || state.selectedWalletId),
    }
  }, [location.state, idParam])

  const initialSelectedWalletId = idParam ?? locationState.selectedWalletId

  const [selectedWalletId, setSelectedWalletId] = useState<string | undefined>(
    initialSelectedWalletId
  )

  // Persist selected wallet into location state so navigate(-1) restores it
  useEffect(() => {
    if (!selectedWalletId || selectedWalletId === locationState.selectedWalletId) return
    navigate(location.pathname, {
      replace: true,
      state: { ...locationState, selectedWalletId },
    })
  }, [selectedWalletId, locationState, navigate, location.pathname])

  // Pre-select default wallet if enabled; otherwise first enabled wallet.
  // Clear selection if none is enabled so PSP list is not fetched.
  useEffect(() => {
    if (isWalletsLoading || wallets.length === 0) return
    if (isCFD && isEquityLoading) return

    const current = wallets.find((w) => w.id === selectedWalletId)
    if (current && !getWithdrawalWalletDisabledReason(current, hasNegativeEquity)) return

    const enabledWallets = wallets.filter(
      (w) => !getWithdrawalWalletDisabledReason(w, hasNegativeEquity)
    )

    if (enabledWallets.length === 0) {
      if (selectedWalletId !== undefined) setSelectedWalletId(undefined)
      return
    }

    const next = enabledWallets.find((w) => w.isDefault) ?? enabledWallets[0]
    if (next.id === selectedWalletId) return
    setSelectedWalletId(next.id)
  }, [isWalletsLoading, wallets, selectedWalletId, hasNegativeEquity, isCFD, isEquityLoading])

  const selectedWallet = useMemo(() => {
    if (isWalletsLoading) return undefined
    if (isCFD && isEquityLoading) return undefined
    const wallet = wallets.find((w) => w.id === selectedWalletId)
    if (!wallet) return undefined
    if (getWithdrawalWalletDisabledReason(wallet, hasNegativeEquity)) return undefined
    return wallet
  }, [wallets, selectedWalletId, hasNegativeEquity, isCFD, isEquityLoading, isWalletsLoading])

  return {
    selectedWalletId,
    setSelectedWalletId,
    selectedWallet,
    locationState,
  }
}
